import { df } from '@/plugins/firebase'
import htmlDocx from 'html-docx-js/dist/html-docx'

// Items are read from location_pricelist/{locationId}/items (not companies/items) —
// that collection duplicates the master item fields below AND carries the
// location-scoped price/tax/category data, so both plain and computed
// attributes can come from the same document.
export const ITEM_ATTRIBUTE_OPTIONS = [
  { value: 'name', label: 'Naziv', path: 'name' },
  { value: 'code', label: 'Šifra', path: 'code' },
  { value: 'barcode', label: 'Barkod', path: 'barcode' },
  { value: 'type', label: 'Vrsta artikla', path: 'type' },
  { value: 'storage_unit', label: 'Jedinica mjere (skladišna)', path: 'storage_unit' },
  { value: 'selling_unit', label: 'Jedinica mjere (prodajna)', path: 'selling_unit' },
  { value: 'price', label: 'Cijena' },
  { value: 'vat', label: 'PDV' },
  { value: 'category', label: 'Kategorija' }
]

function getByPath (obj, path) {
  return path.split('.').reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj)
}

// `prices` is a map keyed by currency code, each entry `{ active, price, taxes }`
// (price in cents). Picks the entry marked active, falling back to the first one.
function activePriceEntry (item) {
  if (!item.prices) return null
  const entries = Object.values(item.prices)
  return entries.find(p => p.active) || entries[0] || null
}

export function resolveAttributeValue (item, attributeField) {
  if (!item) return ''
  if (attributeField === 'price') {
    const entry = activePriceEntry(item)
    return entry && typeof entry.price === 'number' ? `${(entry.price / 100).toFixed(2)} €` : ''
  }
  if (attributeField === 'vat') {
    const entry = activePriceEntry(item)
    const rate = entry && entry.taxes && entry.taxes[0] ? entry.taxes[0].rate : undefined
    // rate is stored scaled by 100 (e.g. 2500 means 25%)
    return rate === undefined ? '' : `${rate / 100}%`
  }
  if (attributeField === 'category') {
    return (item.categories || []).map(c => c.name).filter(Boolean).join(', ')
  }
  const option = ITEM_ATTRIBUTE_OPTIONS.find(o => o.value === attributeField)
  if (!option || !option.path) return ''
  const value = getByPath(item, option.path)
  if (value === undefined || value === null) return ''
  return String(value)
}

export async function getTemplate (companyId) {
  const doc = await df.doc(`declaration_templates/${companyId}`).get()
  if (doc.exists) return doc.data()
  return { company_id: companyId, fields: [] }
}

export function effectiveManualValue (field) {
  if (field.value !== undefined && field.value !== null && field.value !== '') return field.value
  return field.default_value || ''
}

export async function propagateTemplateToDeclarations (companyId, templateFields, removedKeys = []) {
  const snapshot = await df.collection('declarations').where('company_id', '==', companyId).get()
  const updates = []

  snapshot.forEach(docSnap => {
    const declaration = docSnap.data()
    let fields = declaration.fields || []
    let changed = false

    if (removedKeys.length > 0) {
      const before = fields.length
      fields = fields.filter(f => !removedKeys.includes(f.key))
      if (fields.length !== before) changed = true
    }

    templateFields.forEach(tf => {
      const idx = fields.findIndex(f => f.key === tf.key)
      if (idx === -1) {
        fields.push({ ...tf, source: 'template' })
        changed = true
        return
      }
      const current = fields[idx]
      if (current.source !== 'template') return
      const isSame = current.label === tf.label &&
        current.type === tf.type &&
        current.attribute_field === tf.attribute_field &&
        current.column === tf.column &&
        current.order === tf.order &&
        current.printaj === tf.printaj &&
        current.default_value === tf.default_value
      if (!isSame) {
        const updatedField = { ...tf, source: 'template' }
        if (current.value !== undefined) updatedField.value = current.value
        fields[idx] = updatedField
        changed = true
      }
    })

    if (changed) {
      updates.push(df.doc(`declarations/${docSnap.id}`).update({ fields }))
    }
  })

  await Promise.all(updates)
}

// Items whose declaration already carries an explicit (non-empty) value for this
// field — i.e. would NOT pick up a changed default_value automatically, because
// they have real stored data (manual entry or override), not just the fallback.
export async function getFieldValueOverrides (companyId, fieldKey) {
  const snapshot = await df.collection('declarations').where('company_id', '==', companyId).get()
  const items = []

  snapshot.forEach(docSnap => {
    const declaration = docSnap.data()
    const field = (declaration.fields || []).find(f => f.key === fieldKey)
    if (!field) return
    if (field.type === 'manual' && field.value !== undefined && field.value !== null && field.value !== '') {
      items.push({ item_id: declaration.item_id, item_code: declaration.item_code, name: declaration.item_name || declaration.item_id })
    }
  })

  return items
}

// Force-overwrites the stored value for this field on every declaration that has
// one — used when the admin explicitly confirms a new default should replace
// existing per-item values too (including overrides).
export async function forceApplyValueToAll (companyId, fieldKey, newValue) {
  const snapshot = await df.collection('declarations').where('company_id', '==', companyId).get()
  const updates = []

  snapshot.forEach(docSnap => {
    const declaration = docSnap.data()
    const fields = declaration.fields || []
    const idx = fields.findIndex(f => f.key === fieldKey)
    if (idx === -1 || fields[idx].value === newValue) return
    fields[idx] = { ...fields[idx], value: newValue }
    updates.push(df.doc(`declarations/${docSnap.id}`).update({ fields }))
  })

  await Promise.all(updates)
}

export async function saveTemplateFields (companyId, fields, removedKeys = []) {
  await df.doc(`declaration_templates/${companyId}`).set({ company_id: companyId, fields })
  await propagateTemplateToDeclarations(companyId, fields, removedKeys)
}

export async function canDeleteTemplateField (companyId, fieldKey) {
  const snapshot = await df.collection('declarations').where('company_id', '==', companyId).get()
  const blockingItems = []

  snapshot.forEach(docSnap => {
    const declaration = docSnap.data()
    const field = (declaration.fields || []).find(f => f.key === fieldKey)
    if (!field) return
    const hasOverride = field.source === 'override'
    const hasValue = field.type === 'manual' && field.value !== undefined && field.value !== null && field.value !== ''
    if (hasOverride || hasValue) {
      blockingItems.push({ item_id: declaration.item_id, item_code: declaration.item_code, name: declaration.item_name || declaration.item_id })
    }
  })

  return { allowed: blockingItems.length === 0, blockingItems }
}

export function buildDefaultFields (templateFields) {
  return templateFields.map(tf => ({ ...tf, source: 'template' }))
}

export function hasMeaningfulData (fields) {
  return fields.some(f =>
    f.source === 'override' ||
    (f.type === 'manual' && f.value !== undefined && f.value !== null && f.value !== '')
  )
}

export async function getAllActiveDeclarationsForCatalog (companyId, locationId, templateFields) {
  const itemsSnapshot = await df.collection(`location_pricelist/${locationId}/items`)
    .where('status', '==', 'OK')
    .orderBy('name')
    .get()
  const items = itemsSnapshot.docs.map(d => ({ ...d.data(), id: d.id }))

  const declarationsSnapshot = await df.collection('declarations').where('company_id', '==', companyId).get()
  const declarationByItemId = {}
  declarationsSnapshot.forEach(docSnap => {
    const data = docSnap.data()
    declarationByItemId[data.item_id] = data
  })

  return items
    .map(item => {
      const existing = declarationByItemId[item.id]
      return {
        item,
        active: existing ? existing.active : true,
        fields: existing ? existing.fields : buildDefaultFields(templateFields)
      }
    })
    .filter(entry => entry.active !== false)
    .map(entry => ({ item: entry.item, fields: entry.fields }))
}

export async function saveCatalogPrintSettings (companyId, settings) {
  await df.doc(`declaration_templates/${companyId}`).set({ catalog_print_settings: settings }, { merge: true })
}

export function htmlToDocxBlob (innerHtml) {
  return htmlDocx.asBlob(`<html><head><meta charset="utf-8"></head><body>${innerHtml}</body></html>`)
}

export function downloadBlob (blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Direct per-id document fetch (not an 'in' query) — robust regardless of any
// mismatch between an item's own `id` field and its real Firestore doc id.
export async function getDeclarationsByIds (companyId, locationId, itemIds, templateFields) {
  const items = await Promise.all(
    itemIds.map(id => df.doc(`location_pricelist/${locationId}/items/${id}`).get())
  )
  const declarations = await Promise.all(
    itemIds.map(id => df.doc(`declarations/${id}`).get())
  )

  const declarationById = {}
  declarations.forEach(docSnap => {
    if (docSnap.exists) declarationById[docSnap.id] = docSnap.data()
  })

  return items
    .filter(docSnap => docSnap.exists)
    .map(docSnap => {
      const item = { ...docSnap.data(), id: docSnap.id }
      const existing = declarationById[item.id]
      return {
        item,
        fields: existing ? existing.fields : buildDefaultFields(templateFields)
      }
    })
}

export async function saveLabelPrintSettings (companyId, settings) {
  await df.doc(`declaration_templates/${companyId}`).set({ label_print_settings: settings }, { merge: true })
}
